import React, { useEffect, useState } from 'react';
import {
  clampRgbChannel,
  hexForPicker,
  parseColorToRgb,
  rgbPartsToCss,
  rgbPartsToHex,
} from '../../utils/rgbColor';

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export default function RichTextRgbColorPopover({
  open,
  anchorRect,
  title,
  initialValue,
  onApply,
  onClear,
  onClose,
}) {
  const parsed = parseColorToRgb(initialValue || '#111827', '#111827');
  const [r, setR] = useState(parsed.r);
  const [g, setG] = useState(parsed.g);
  const [b, setB] = useState(parsed.b);
  const [alpha, setAlpha] = useState(Math.round((parsed.alpha ?? 1) * 100));

  useEffect(() => {
    if (!open) return;
    const next = parseColorToRgb(initialValue || '#111827', '#111827');
    setR(next.r);
    setG(next.g);
    setB(next.b);
    setAlpha(Math.round((next.alpha ?? 1) * 100));
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !anchorRect) return null;

  const hex = rgbPartsToHex(r, g, b);
  const css = rgbPartsToCss(r, g, b, alpha / 100);

  const patchChannel = (setter) => (e) => setter(clampRgbChannel(e.target.value));

  const applyCurrent = () => onApply?.(css);

  const style = {
    position: 'fixed',
    top: Math.min(anchorRect.bottom + 6, window.innerHeight - 280),
    left: Math.min(anchorRect.left, window.innerWidth - 280),
    zIndex: 9200,
  };

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[9199] cursor-default bg-transparent border-0 p-0"
        aria-label="Fermer"
        onClick={onClose}
      />
      <div
        className="rte-rgb-popover w-[268px] rounded-lg border border-border bg-popover text-popover-foreground shadow-lg p-3 space-y-3"
        style={style}
        role="dialog"
        aria-label={title}
      >
        <p className="text-xs font-semibold text-foreground">{title}</p>

        <div className="flex items-center gap-3">
          <input
            type="color"
            className="h-10 w-12 rounded-md border border-border shrink-0 cursor-pointer bg-background p-0.5"
            value={hexForPicker(hex)}
            onChange={(e) => {
              const p = parseColorToRgb(e.target.value, hex);
              setR(p.r);
              setG(p.g);
              setB(p.b);
            }}
            aria-label="Pipette"
          />
          <div
            className="flex-1 h-10 rounded-md border border-border bg-[length:8px_8px] bg-[position:0_0,4px_4px] bg-[image:linear-gradient(45deg,#ccc_25%,transparent_25%),linear-gradient(-45deg,#ccc_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#ccc_75%),linear-gradient(-45deg,transparent_75%,#ccc_75%)]"
            style={{ backgroundColor: css }}
            title={css}
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="R">
            <input
              type="number"
              min={0}
              max={255}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums"
              value={r}
              onChange={patchChannel(setR)}
            />
          </Field>
          <Field label="G">
            <input
              type="number"
              min={0}
              max={255}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums"
              value={g}
              onChange={patchChannel(setG)}
            />
          </Field>
          <Field label="B">
            <input
              type="number"
              min={0}
              max={255}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm tabular-nums"
              value={b}
              onChange={patchChannel(setB)}
            />
          </Field>
        </div>

        <Field label={`Opacité (${alpha}%)`}>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            className="w-full"
            value={alpha}
            onChange={(e) => setAlpha(Number(e.target.value))}
          />
        </Field>

        <p className="text-[11px] text-muted-foreground font-mono truncate" title={css}>
          {css}
        </p>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-muted"
            onClick={() => {
              onClear?.();
              onClose?.();
            }}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="text-xs font-medium bg-primary text-primary-foreground px-3 py-1.5 rounded-md hover:opacity-90"
            onClick={() => {
              applyCurrent();
              onClose?.();
            }}
          >
            Appliquer
          </button>
        </div>
      </div>
    </>
  );
}
